<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaStatusIndicator, { type GaiaIndicatorStatus } from '../base/GaiaStatusIndicator.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFonts, gaiaNeutral, gaiaTracking } from '../themes/tokens';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  /** Mono count next to the title. */
  count: {
    type: [String, Number],
    default: undefined,
  },
  /** Column status dot (AI Review/Todo = pending, In Review = warning, Done = success). */
  status: {
    type: String as PropType<GaiaIndicatorStatus>,
    default: 'pending',
  },
  /** Info tooltip text next to the header (e.g. the AI Review auto-move note). */
  note: {
    type: String,
    default: undefined,
  },
  /** Drop-target highlight while a card is dragged over. */
  dragOver: {
    type: Boolean,
    default: false,
  },
  /** Render the dashed "Nothing here yet" placeholder. */
  empty: {
    type: Boolean,
    default: false,
  },
  /**
   * Board density (the mock's Comfortable/Compact switch), tightening the card
   * stack's gap and padding. Pass the same value to the cards inside.
   */
  density: {
    type: String as PropType<'comfortable' | 'compact'>,
    default: 'comfortable',
  },
});

const border = gaiaNeutral.borderCard;
const bg = gaiaNeutral.surfaceSoft;
const dragBg = gaiaNeutral.surfaceTint;
const countColor = gaiaNeutral.textFaint;
const emptyColor = gaiaNeutral.textMuted;
const headBorder = gaiaNeutral.borderSoft;
const borderHover = gaiaNeutral.borderHover;
const emptyBorder = gaiaNeutral.border;
const monoFont = gaiaFonts.mono;
const snugTracking = gaiaTracking.snug;

// The stack is CSS (see the rule below), so density arrives through v-bind.
const compact = computed(() => props.density === 'compact');
const stackGap = computed(() => (compact.value ? '6px' : '10px'));
const stackPad = computed(() => (compact.value ? '8px' : '12px'));
</script>

<template>
  <!-- Kanban column from the Reviews board mock: status-dot header with mono
       count and optional info tooltip, scrollable card stack (default slot),
       dashed empty placeholder, drop-target highlight. -->
  <n-flex vertical :size="0" class="gaia-kanban-column" :class="{ over: props.dragOver }">
    <n-flex
      align="center"
      :size="8"
      :wrap="false"
      :title="props.note"
      :style="{ flex: 'none', padding: '14px 14px 12px', borderBottom: `1px solid ${headBorder}` }"
    >
      <gaia-status-indicator :status="props.status" />
      <gaia-text size="body" weight="bold" tone="ink" :style="{ letterSpacing: snugTracking }">
        {{ props.title }}
      </gaia-text>
      <gaia-text
        v-if="props.count !== undefined"
        size="meta"
        tone="faint"
        :style="{ fontFamily: monoFont }"
      >
        {{ props.count }}
      </gaia-text>
      <n-tooltip v-if="props.note" trigger="hover">
        <template #trigger>
          <svg
            class="info"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5" />
            <path d="M12 7.6v.6" />
          </svg>
        </template>
        {{ props.note }}
      </n-tooltip>
    </n-flex>
    <div class="stack">
      <slot />
      <n-flex v-if="props.empty" vertical align="center" justify="center" :size="8" class="empty">
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
          <path d="M8 9.5h8M8 14h5" />
        </svg>
        <gaia-text size="meta" tone="muted">Nothing here yet</gaia-text>
      </n-flex>
      <!-- Below the cards, inside the scroller: the column's own pagination.
           `searchReview` pages per kanban state, so "Load more" belongs to the
           column, not the board. -->
      <div v-if="$slots.footer" :style="{ flex: 'none' }"><slot name="footer" /></div>
    </div>
  </n-flex>
</template>

<style scoped>
.gaia-kanban-column {
  border: 1px solid v-bind(border);
  border-radius: 12px;
  background: v-bind(bg);
  min-height: 0;
  max-height: 100%;
  transition:
    background 0.15s,
    border-color 0.15s;
}

.gaia-kanban-column.over {
  background: v-bind(dragBg);
  border-color: v-bind(borderHover);
}

.info {
  margin-left: auto;
  flex: none;
  color: v-bind(countColor);
  cursor: help;
}

/* Stays CSS rather than becoming an n-flex: a column with no cards and `empty`
   unset leaves this childless, and n-flex renders nothing at all when its slot
   is empty — the column would lose this flex:1 and its padding. */
.stack {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: v-bind(stackGap);
  padding: v-bind(stackPad);
}

.empty {
  flex: 1;
  min-height: 120px;
  border: 1px dashed v-bind(emptyBorder);
  border-radius: 10px;
  /* Also the svg's currentColor stroke, so it stays on the box. */
  color: v-bind(emptyColor);
}
</style>
